import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import jsPDF from 'jspdf';
import { 
  Info, X, Package, Truck, Warehouse, TrendingUp, FileText, 
  Settings, ShoppingCart, Users, Globe, Database, Shield, 
  Bell, Clock, CheckCircle, AlertTriangle, BarChart3, 
  Activity, MapPin, Calendar, DollarSign, Zap, Layers,
  GitBranch, GitCommit, GitPullRequest, GitMerge, Workflow,
  UserCheck, ArrowRight, ArrowLeft, Play, Pause, Stop,
  RotateCcw, Filter, Search, List, Grid, PieChart,
  Monitor, Smartphone, Wifi, Lock, Eye, EyeOff, Save,
  RefreshCw, Download, Upload, Trash2, Edit, Plus,
  Check, Sun, Moon, Clock4, CalendarDays,
  Target, Brain, Palette, Key, Database as DatabaseIcon
} from 'lucide-react';
import { useChainsTheme } from '../../context/ChainsThemeContext';

const SystemInfo = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { isDarkMode } = useChainsTheme();
  const modalRef = useRef(null);

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      
      // Title
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Chains System Information', margin, 30);
      
      // Subtitle
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Comprehensive overview of system features, functionality, and components', margin, 40);
      
      let yPosition = 60;
      
      // Function to add section
      const addSection = (title, features) => {
        // Section title
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(title, margin, yPosition);
        yPosition += 10;
        
        // Features
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        features.forEach((feature, index) => {
          const lines = pdf.splitTextToSize(`• ${feature}`, contentWidth - 10);
          if (yPosition + (lines.length * 5) > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin + 20;
          }
          pdf.text(lines, margin + 5, yPosition);
          yPosition += lines.length * 5 + 2;
        });
        yPosition += 10;
      };
      
      // Add all sections
      Object.values(systemFeatures).forEach(section => {
        addSection(section.title, section.features);
      });
      
      // Add technical specs
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Technical Specifications', margin, yPosition);
      yPosition += 10;
      
      Object.values(technicalSpecs).forEach(section => {
        addSection(section.title, section.specs);
      });
      
      // System status
      yPosition += 10;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('System Status', margin, yPosition);
      yPosition += 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('• System Status: Online', margin, yPosition);
      yPosition += 6;
      pdf.text('• Uptime: 99.9%', margin, yPosition);
      yPosition += 6;
      pdf.text('• Monitoring: 24/7', margin, yPosition);
      yPosition += 6;
      pdf.text('• Version: v2.1.0', margin, yPosition);
      
      // Save the PDF
      pdf.save('Chains-System-Information.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const systemFeatures = {
    overview: {
      title: "Overall System Description",
      features: [
        "Next-generation supply chain management platform for modern businesses",
        "End-to-end supply chain visibility from procurement to delivery",
        "AI-powered demand forecasting and inventory optimization",
        "Multi-warehouse inventory management with real-time synchronization",
        "Integrated logistics and transportation management system",
        "Advanced analytics and predictive insights for supply chain optimization"
      ]
    },
    core: {
      title: "Core Supply Chain Management",
      features: [
        "End-to-end supply chain visibility and tracking with real-time updates",
        "Real-time inventory management across multiple warehouses and locations",
        "Automated demand forecasting and planning with machine learning",
        "Multi-vendor procurement and supplier management with performance tracking",
        "Integrated logistics and route optimization for cost efficiency",
        "Comprehensive reporting and analytics dashboard with KPI tracking"
      ]
    },
    modules: {
      title: "Module Components",
      features: [
        "Dashboard - Real-time KPIs and performance metrics",
        "Purchase Requests - Approval workflows and budget tracking",
        "Procurement - Vendor management and purchase orders",
        "Logistics - Route optimization and vehicle tracking",
        "Warehouse - Inventory management and storage analytics",
        "Planning - Demand forecasting and supply planning",
        "Reports - Advanced analytics and KPI reporting",
        "Settings - System configuration and user management"
      ]
    },
    workflows: {
      title: "Workflow & Automation",
      features: [
        "Automated approval workflows with role-based permissions",
        "Configurable business rules and triggers",
        "Multi-step approval processes with thresholds",
        "Real-time notifications and alerts",
        "Workflow execution tracking and analytics",
        "Custom workflow builder and templates"
      ]
    },
    analytics: {
      title: "Analytics & Reporting",
      features: [
        "Real-time performance dashboards",
        "Advanced data visualization with charts and graphs",
        "Custom report builder with drag-and-drop interface",
        "KPI tracking and performance metrics",
        "Trend analysis and forecasting models",
        "Export capabilities (PDF, Excel, CSV)"
      ]
    },
    integrations: {
      title: "System Integrations",
      features: [
        "ERP system integration (SAP, Oracle, etc.)",
        "WMS (Warehouse Management System) connectivity",
        "TMS (Transportation Management System) integration",
        "Supplier portal and API connections",
        "Third-party logistics provider integration",
        "Financial system integration for cost tracking"
      ]
    },
    security: {
      title: "Security & Compliance",
      features: [
        "Role-based access control (RBAC)",
        "Multi-factor authentication (MFA)",
        "Data encryption at rest and in transit",
        "Audit trails and activity logging",
        "GDPR and data privacy compliance",
        "Regular security updates and patches"
      ]
    },
    performance: {
      title: "Performance & Scalability",
      features: [
        "High-performance database architecture",
        "Caching mechanisms for improved response times",
        "Load balancing and auto-scaling capabilities",
        "Real-time data synchronization",
        "Mobile-responsive design for all devices",
        "Offline capability with data synchronization"
      ]
    },
    data: {
      title: "Data Management",
      features: [
        "Centralized data warehouse architecture",
        "Real-time data processing and analytics",
        "Data backup and disaster recovery",
        "Data validation and quality controls",
        "Historical data archiving and retention",
        "Data import/export with validation"
      ]
    },
    notifications: {
      title: "Notification System",
      features: [
        "Real-time push notifications",
        "Email notifications with customizable templates",
        "SMS alerts for critical events",
        "In-app notification center",
        "Escalation workflows for urgent matters",
        "Notification preferences and scheduling"
      ]
    },
    mobile: {
      title: "Mobile Capabilities",
      features: [
        "Progressive Web App (PWA) support",
        "Native mobile app for iOS and Android",
        "Offline data access and synchronization",
        "Mobile-optimized workflows and forms",
        "Barcode/QR code scanning capabilities",
        "GPS tracking and location services"
      ]
    }
  };

  const technicalSpecs = {
    architecture: {
      title: "System Architecture",
      specs: [
        "React.js frontend with modern hooks and context",
        "Component-based modular architecture",
        "Responsive design with Tailwind CSS",
        "Framer Motion for smooth animations",
        "Recharts for data visualization",
        "Lucide React for consistent iconography"
      ]
    },
    dataFlow: {
      title: "Data Flow & State Management",
      specs: [
        "Centralized theme context for consistent theming",
        "Component-level state management with React hooks",
        "Real-time data updates and synchronization",
        "Optimistic UI updates for better user experience",
        "Error handling and fallback mechanisms",
        "Data persistence and caching strategies"
      ]
    },
    components: {
      title: "Component Structure",
      specs: [
        "Layout components with responsive design",
        "Dashboard components with real-time data",
        "Form components with validation",
        "Chart components for data visualization",
        "Modal components for overlays and dialogs",
        "Navigation components with active states"
      ]
    }
  };

  const FeatureSection = ({ title, features, icon: Icon }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`backdrop-blur-sm rounded-2xl p-6 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-center space-x-3 mb-4">
        <div className={`p-2 rounded-lg ${
          isDarkMode ? 'bg-white/10' : 'bg-gray-100'
        }`}>
          <Icon className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
        </div>
        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {title}
        </h3>
      </div>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <motion.li
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`flex items-start space-x-2 ${
              isDarkMode ? 'text-white/80' : 'text-gray-700'
            }`}
          >
            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <span className="text-sm">{feature}</span>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );

  return (
    <>
      {/* Floating Info Button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 p-4 rounded-full shadow-lg transition-all ${
          isDarkMode 
            ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white hover:shadow-xl' 
            : 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white hover:shadow-xl'
        }`}
      >
        <Info className="w-6 h-6" />
      </motion.button>

      {/* System Information Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl ${
                isDarkMode 
                  ? 'bg-gray-900 border border-white/20' 
                  : 'bg-white border border-gray-200'
              }`}
            >
              {/* Header */}
              <div className={`sticky top-0 z-10 p-6 border-b ${
                isDarkMode ? 'border-white/20' : 'border-gray-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-xl ${
                      isDarkMode ? 'bg-white/10' : 'bg-blue-100'
                    }`}>
                      <Info className={`w-6 h-6 ${isDarkMode ? 'text-white' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Chains System Information
                      </h1>
                      <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>
                        Comprehensive overview of system features, functionality, and components
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={exportToPDF}
                      disabled={isExporting}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${
                        isDarkMode 
                          ? 'hover:bg-white/10 text-white' 
                          : 'hover:bg-gray-100 text-gray-600'
                      } ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isExporting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm">Exporting...</span>
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          <span className="text-sm">Export PDF</span>
                        </>
                      )}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setIsOpen(false)}
                      className={`p-2 rounded-lg transition-all ${
                        isDarkMode 
                          ? 'hover:bg-white/10 text-white' 
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >
                      <X className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-8">
                {/* System Overview */}
                <div className="grid grid-cols-1 gap-6">
                  <FeatureSection 
                    title={systemFeatures.overview.title}
                    features={systemFeatures.overview.features}
                    icon={Package}
                  />
                </div>

                {/* Core & Modules */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FeatureSection 
                    title={systemFeatures.core.title}
                    features={systemFeatures.core.features}
                    icon={Target}
                  />
                  <FeatureSection 
                    title={systemFeatures.modules.title}
                    features={systemFeatures.modules.features}
                    icon={Layers}
                  />
                </div>

                {/* Workflows & Analytics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FeatureSection 
                    title={systemFeatures.workflows.title}
                    features={systemFeatures.workflows.features}
                    icon={Workflow}
                  />
                  <FeatureSection 
                    title={systemFeatures.analytics.title}
                    features={systemFeatures.analytics.features}
                    icon={BarChart3}
                  />
                </div>

                {/* Integrations & Security */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FeatureSection 
                    title={systemFeatures.integrations.title}
                    features={systemFeatures.integrations.features}
                    icon={Globe}
                  />
                  <FeatureSection 
                    title={systemFeatures.security.title}
                    features={systemFeatures.security.features}
                    icon={Shield}
                  />
                </div>

                {/* Performance & Data */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FeatureSection 
                    title={systemFeatures.performance.title}
                    features={systemFeatures.performance.features}
                    icon={Zap}
                  />
                  <FeatureSection 
                    title={systemFeatures.data.title}
                    features={systemFeatures.data.features}
                    icon={DatabaseIcon}
                  />
                </div>

                {/* Notifications & Mobile */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FeatureSection 
                    title={systemFeatures.notifications.title}
                    features={systemFeatures.notifications.features}
                    icon={Bell}
                  />
                  <FeatureSection 
                    title={systemFeatures.mobile.title}
                    features={systemFeatures.mobile.features}
                    icon={Smartphone}
                  />
                </div>

                {/* Technical Specifications */}
                <div className="space-y-6">
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Technical Specifications
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <FeatureSection 
                      title={technicalSpecs.architecture.title}
                      features={technicalSpecs.architecture.specs}
                      icon={Monitor}
                    />
                    <FeatureSection 
                      title={technicalSpecs.dataFlow.title}
                      features={technicalSpecs.dataFlow.specs}
                      icon={GitBranch}
                    />
                    <FeatureSection 
                      title={technicalSpecs.components.title}
                      features={technicalSpecs.components.specs}
                      icon={Layers}
                    />
                  </div>
                </div>

                {/* System Status */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`backdrop-blur-sm rounded-2xl p-6 ${
                    isDarkMode 
                      ? 'bg-white/10 border border-white/20' 
                      : 'bg-white/70 border border-gray-200/50'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`p-2 rounded-lg ${
                      isDarkMode ? 'bg-white/10' : 'bg-gray-100'
                    }`}>
                      <Activity className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
                    </div>
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      System Status
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-2 mb-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Online
                        </span>
                      </div>
                      <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>System Status</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>99.9%</p>
                      <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Uptime</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>24/7</p>
                      <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Monitoring</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>v2.1.0</p>
                      <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Version</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SystemInfo; 